/**
 * Настройки маркетинга.
 *  • «Доступ»          — управление всеми кабинетами Я.Директа (+ цели/атрибуция по кабинету).
 *  • «Сделки и воронка» — сегменты: своя UTM-разметка + свои этапы для расчёта конверсии/CPO.
 * TG-стиль: только существующие токены/классы (.card/.btn/.input-sm/var(--brick)…).
 * Единые отступы и ширины контролов через grid; выбранные состояния — явный зелёный.
 */
import { useState, useEffect, useMemo } from "react";
import type { CSSProperties } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Icon } from "@/components/prototype/Icon";
import { Spinner } from "@/components/Spinner";
import { useAuth } from "@/auth/AuthContext";
import {
  fetchAmoOverride, saveAmoOverride,
  fetchMarketingAccounts, fetchMarketingOAuthStatus, connectMarketing,
  fetchTargets, saveTargets, fetchMarketingOverview,
  deleteMarketingAccount, updateMarketingAccount, syncMarketing,
  fetchMarketingMetrikaCounters, importMarketingGoals, fetchMarketingGoals, saveMarketingGoals,
  fetchAmoCustomFields, fetchAmoStages, fetchAmoFieldValues, fetchCrmConfig, saveCrmConfig,
  fetchCallsMode, saveCallsMode,
  fetchSuccessStages, saveSuccessStages,
  fetchCrmTypes, fetchCrmStatus, fetchCrmConnections, saveCrmConnections,
  fetchAdSources, fetchAdConnections, saveAdConnections,
  type MarketingAccount, type AmoStage, type AmoCustomField, type CrmConnection, type AdConnection,
} from "@/api/dashboard";

// --- общие стили (единые отступы/ширины) ---
const GREEN = "#4fae4e";          // = var(--brick) в marketing (production-tg.css)
const fullCtl: CSSProperties = { width: "100%", boxSizing: "border-box" };
const fieldLabel: CSSProperties = { fontSize: 11.5, color: "var(--ink-3)", marginBottom: 4, display: "block", fontWeight: 600 };
const muted: CSSProperties = { fontSize: 12.5, color: "var(--mute)", lineHeight: 1.5 };
const sectionTitle: CSSProperties = { fontSize: 13, fontWeight: 700, margin: "0 0 10px" };
const cardStyle: CSSProperties = { padding: 18, boxShadow: "0 1px 3px rgba(20,20,20,0.05)" };

function chipStyle(on: boolean, color?: string | null): CSSProperties {
  return {
    padding: "6px 12px", fontSize: 12, borderRadius: 8, cursor: "pointer",
    border: "1px solid var(--line)", lineHeight: 1.2,
    background: on ? GREEN : "var(--card)",
    color: on ? "#fff" : "var(--ink)",
    borderColor: on ? GREEN : "var(--line)",
    borderLeft: color ? `3px solid ${on ? "#fff" : color}` : undefined,
  };
}

type PillKind = "ok" | "warn" | "err" | "muted";
function Pill({ text, kind }: { text: string; kind: PillKind }) {
  const c: Record<PillKind, { bg: string; fg: string }> = {
    ok: { bg: "#eaf6ee", fg: "#1f7a3f" }, warn: { bg: "#fbf3e2", fg: "#8a6300" },
    err: { bg: "#fdeceb", fg: "#b23b30" }, muted: { bg: "#f1f1f3", fg: "var(--ink-2)" },
  };
  return <span style={{ fontSize: 11, fontWeight: 600, padding: "2px 9px", borderRadius: 20, background: c[kind].bg, color: c[kind].fg }}>{text}</span>;
}

// Брендовые логотипы (app-icon: градиент + squircle + блик; узнаваемые — SVG-глиф).
type BrandDef = { from: string; to: string; fg: string; mark: string; svg?: "tg" | "avito" | "gads" };
const BRAND: Record<string, BrandDef> = {
  yandex_direct: { from: "#FF4D2E", to: "#E2330F", fg: "#fff", mark: "Я" },
  vk_ads:        { from: "#3C8AF0", to: "#0A66D6", fg: "#fff", mark: "VK" },
  telegram_ads:  { from: "#37BBFE", to: "#1E96D2", fg: "#fff", mark: "TG", svg: "tg" },
  avito:         { from: "#ffffff", to: "#f3f3f5", fg: "#333", mark: "A", svg: "avito" },
  google_ads:    { from: "#ffffff", to: "#f6f7f9", fg: "#333", mark: "G", svg: "gads" },
  mytarget:      { from: "#FF4438", to: "#E1251B", fg: "#fff", mark: "mt" },
  yandex_market: { from: "#FFD94D", to: "#FFC400", fg: "#21201F", mark: "М" },
  "2gis":        { from: "#37C24A", to: "#1BA136", fg: "#fff", mark: "2Г" },
  ok:            { from: "#FF9F2E", to: "#EE8208", fg: "#fff", mark: "OK" },
  amocrm:    { from: "#4FB3DA", to: "#2E8FBF", fg: "#fff", mark: "amo" },
  roistat:   { from: "#FF8A4C", to: "#FF6B2C", fg: "#fff", mark: "R" },
  bitrix24:  { from: "#4FD2FB", to: "#2FC7F7", fg: "#fff", mark: "B24" },
  calltouch: { from: "#4F9CF5", to: "#1B7CED", fg: "#fff", mark: "ct" },
  comagic:   { from: "#9670FF", to: "#7C4DFF", fg: "#fff", mark: "co" },
  other:     { from: "#f1f1f4", to: "#e3e3e8", fg: "#6b6b75", mark: "+" },
};

function brandGlyph(kind: BrandDef["svg"], s: number) {
  const v = s * 0.66;
  if (kind === "tg") return (
    <svg width={v} height={v} viewBox="0 0 24 24"><path fill="#fff" d="M21.9 4.4 2.7 11.8c-1.1.4-1.1 1.1-.2 1.4l4.9 1.5 1.8 5.8c.2.5.4.7.8.7.4 0 .6-.2.9-.5l2.4-2.3 4.9 3.6c.9.5 1.5.2 1.7-.8l3.1-14.6c.3-1.2-.5-1.8-1.1-1.2z"/><path fill="#cfeaff" d="m9.7 14.9-.3 3.7 1.3-3.5 7.1-6.6c.3-.3 0-.5-.4-.3L8 13.9z"/></svg>
  );
  if (kind === "avito") return (
    <svg width={v} height={v} viewBox="0 0 24 24">
      <circle cx="8" cy="8" r="3.6" fill="#965EEB"/><circle cx="16.6" cy="7.4" r="2.5" fill="#0AF"/>
      <circle cx="7.4" cy="16.6" r="2.5" fill="#04E061"/><circle cx="16" cy="16" r="3.6" fill="#FF4053"/>
    </svg>
  );
  if (kind === "gads") return (
    <svg width={v} height={v} viewBox="0 0 24 24">
      <rect x="6" y="3.5" width="4.6" height="17" rx="2.3" fill="#FBBC04" transform="rotate(-20 8.3 12)"/>
      <rect x="13.4" y="3.5" width="4.6" height="17" rx="2.3" fill="#4285F4" transform="rotate(20 15.7 12)"/>
      <circle cx="8" cy="18.5" r="2.7" fill="#34A853"/>
    </svg>
  );
  return null;
}

function BrandLogo({ id, label, size = 40 }: { id: string; label?: string; size?: number }) {
  const b = BRAND[id] ?? { from: "#5cc47c", to: "#3a9e5b", fg: "#fff", mark: (label || id).slice(0, 1).toUpperCase() };
  return (
    <div style={{
      width: size, height: size, borderRadius: size * 0.28, flex: "0 0 auto",
      background: `linear-gradient(155deg, ${b.from}, ${b.to})`,
      boxShadow: "0 1px 2px rgba(20,20,20,0.18), inset 0 1px 0 rgba(255,255,255,0.30)",
      display: "grid", placeItems: "center", overflow: "hidden",
    }}>
      {b.svg ? brandGlyph(b.svg, size) : (
        <span style={{ fontWeight: 800, fontSize: size * (b.mark.length > 2 ? 0.3 : b.mark.length > 1 ? 0.36 : 0.44),
                       color: b.fg, fontFamily: "var(--mono)", letterSpacing: -0.6 }}>{b.mark}</span>
      )}
    </div>
  );
}

type Tab = "access" | "crm" | "funnel" | "success" | "targets";

export function MarketingSettingsPage() {
  const { user } = useAuth();
  const [tab, setTab] = useState<Tab>("access");
  const isOwner = !!(user && (user.is_superuser || user.role === "owner"));
  if (!isOwner) {
    return (
      <div style={{ padding: 40, maxWidth: 520, margin: "0 auto", textAlign: "center" }}>
        <div style={{ fontSize: 40, marginBottom: 8 }}>🔒</div>
        <h1 style={{ fontSize: 20, fontWeight: 700, margin: 0 }}>Настройки доступны только владельцу</h1>
        <p style={{ color: "var(--mute)", fontSize: 13.5, marginTop: 8, lineHeight: 1.5 }}>
          Управление рекламными кабинетами, CRM-системами и воронкой — для владельца аккаунта.
        </p>
      </div>
    );
  }
  return (
    <div style={{ padding: "20px 16px", maxWidth: 920, margin: "0 auto" }}>
      <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700 }}>Настройки</h1>
      <div style={{ display: "flex", gap: 8, margin: "16px 0 0", flexWrap: "wrap" }}>
        {([["access", "Рекламные кабинеты"], ["crm", "CRM-системы"], ["funnel", "Сделки и воронка"], ["success", "Успешные сделки"], ["targets", "Цели и нормативы"]] as [Tab, string][]).map(([t, label]) => (
          <button key={t} className={`btn btn-sm ${tab === t ? "btn-primary" : ""}`} onClick={() => setTab(t)}>
            {label}
          </button>
        ))}
      </div>
      <div style={{ marginTop: 16 }}>
        {tab === "access" && <AccessTab/>}
        {tab === "crm" && <CrmTab/>}
        {tab === "funnel" && <FunnelConfigTab/>}
        {tab === "success" && <SuccessStagesTab/>}
        {tab === "targets" && <TargetsTab/>}
      </div>
    </div>
  );
}

function AccessTab() {
  const qc = useQueryClient();
  const accountsQ = useQuery({ queryKey: ["marketing/accounts"], queryFn: fetchMarketingAccounts });
  const sourcesQ = useQuery({ queryKey: ["marketing/ad-sources"], queryFn: fetchAdSources });
  const adConnsQ = useQuery({ queryKey: ["marketing/ad-connections"], queryFn: fetchAdConnections });
  // null | "pick" | <source> — мастер добавления
  const [adding, setAdding] = useState<string | null>(null);

  const sources = sourcesQ.data?.sources ?? [];
  const adConns = adConnsQ.data?.connections ?? [];
  const srcLabel = (s: string) => sources.find(x => x.source === s)?.label ?? s;
  const srcWired = (s: string) => sources.find(x => x.source === s)?.wired ?? false;

  const saveAdMut = useMutation({
    mutationFn: (conns: AdConnection[]) => saveAdConnections(conns),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["marketing/ad-connections"] }),
  });

  if (accountsQ.isLoading) return <div style={{ display: "flex", justifyContent: "center", padding: "28px 12px" }}><Spinner size="lg" label="Загрузка…"/></div>;
  const accounts = accountsQ.data ?? [];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <p style={{ ...muted, margin: 0 }}>
        Рекламные кабинеты всех источников. Я.Директ сводится в метрики автоматически; VK/Telegram/Авито/Google
        и др. — конфиг сохраняется, синк подключается поэтапно.
      </p>

      {accounts.length === 0 && adConns.length === 0 && adding === null && (
        <div className="card" style={{ ...cardStyle, textAlign: "center", color: "var(--mute)" }}>
          Кабинетов пока нет. Добавьте первый.
        </div>
      )}

      {accounts.map(acc => <AccountCard key={acc.id} acc={acc}/>)}
      {adConns.map(c => (
        <AdConnCard key={c.id} conn={c} label={srcLabel(c.source)} wired={srcWired(c.source)}
                    onSave={upd => saveAdMut.mutate(adConns.map(x => x.id === c.id ? upd : x))}
                    onRemove={() => saveAdMut.mutate(adConns.filter(x => x.id !== c.id))}/>
      ))}

      {adding === null && (
        <div><button className="btn btn-sm btn-primary" onClick={() => setAdding("pick")}>+ Добавить кабинет</button></div>
      )}

      {adding === "pick" && (
        <div className="card" style={cardStyle}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
            <div style={{ ...sectionTitle, margin: 0 }}>Какой источник подключить?</div>
            <button className="btn btn-sm" onClick={() => setAdding(null)}>Отмена</button>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))", gap: 10 }}>
            {sources.map(s => (
              <button key={s.source} onClick={() => setAdding(s.source)}
                      style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 14px", borderRadius: 12,
                               border: "1px solid var(--line)", background: "var(--card)", cursor: "pointer", textAlign: "left",
                               transition: "border-color .15s, box-shadow .15s" }}
                      onMouseEnter={e => { e.currentTarget.style.borderColor = GREEN; e.currentTarget.style.boxShadow = "0 2px 8px rgba(79,174,78,0.15)"; }}
                      onMouseLeave={e => { e.currentTarget.style.borderColor = "var(--line)"; e.currentTarget.style.boxShadow = "none"; }}>
                <BrandLogo id={s.source} label={s.label} size={34}/>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: 13, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{s.label}</div>
                  <div style={{ fontSize: 10.5, color: s.wired ? "#1f7a3f" : "var(--mute)" }}>{s.wired ? "интегрирован" : "поэтапно"}</div>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {adding === "yandex_direct" && (
        <div className="card" style={cardStyle}>
          <div style={sectionTitle}>Добавить Яндекс.Директ</div>
          <ConnectForm onDone={() => { setAdding(null); qc.invalidateQueries({ queryKey: ["marketing/accounts"] }); }}
                       onCancel={() => setAdding(null)}/>
        </div>
      )}

      {adding != null && adding !== "pick" && adding !== "yandex_direct" && (
        <AdConnForm label={srcLabel(adding)} wired={srcWired(adding)}
                    onSave={fields => { saveAdMut.mutate([...adConns, { id: "", source: adding, ...fields } as AdConnection]); setAdding(null); }}
                    onCancel={() => setAdding(null)}/>
      )}
    </div>
  );
}

function AdConnCard({ conn, label, wired, onSave, onRemove }: {
  conn: AdConnection; label: string; wired: boolean;
  onSave: (c: AdConnection) => void; onRemove: () => void;
}) {
  const [editing, setEditing] = useState(false);
  if (editing) {
    return (
      <AdConnForm label={label} wired={wired} submitLabel="Сохранить"
                  initial={{ name: conn.name, account: conn.account ?? "", api_key: conn.api_key, note: conn.note }}
                  onSave={f => { onSave({ ...conn, ...f }); setEditing(false); }} onCancel={() => setEditing(false)}/>
    );
  }
  return (
    <div className="card" style={cardStyle}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <div style={{ display: "flex", gap: 12, alignItems: "center", minWidth: 0 }}>
          <BrandLogo id={conn.source} label={label}/>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontWeight: 700, fontSize: 15, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              {conn.name || label}
              <Pill text={label} kind="muted"/>
              {!wired && <Pill text="поэтапно" kind="warn"/>}
            </div>
            <div style={{ ...muted, marginTop: 3 }}>
              {conn.account && <>аккаунт {conn.account} · </>}ключ {conn.api_key || "—"}{conn.note && <> · {conn.note}</>}
            </div>
          </div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button className="btn btn-sm" onClick={() => setEditing(true)}>Изменить</button>
          <button className="btn btn-sm" onClick={onRemove}>Удалить</button>
        </div>
      </div>
    </div>
  );
}

function AdConnForm({ label, wired, initial, submitLabel = "Добавить", onSave, onCancel }: {
  label: string; wired: boolean;
  initial?: { name: string; account: string; api_key: string; note: string };
  submitLabel?: string;
  onSave: (f: { name: string; account: string; api_key: string; note: string }) => void; onCancel: () => void;
}) {
  const [name, setName] = useState(initial?.name ?? "");
  const [account, setAccount] = useState(initial?.account ?? "");
  const [apiKey, setApiKey] = useState(initial?.api_key ?? "");
  const [note, setNote] = useState(initial?.note ?? "");
  return (
    <div className="card" style={cardStyle}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
        <BrandLogo id="other" label={label} size={34}/>
        <div style={{ ...sectionTitle, margin: 0 }}>{submitLabel === "Сохранить" ? "Изменить" : "Добавить"} {label}</div>
      </div>
      {!wired && <div style={{ ...muted, marginBottom: 10 }}>Конфиг сохранится. Автоматический синк этого источника подключим поэтапно.</div>}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <div><label style={fieldLabel}>Название</label><input className="input-sm" style={fullCtl} value={name} onChange={e => setName(e.target.value)} placeholder={label}/></div>
        <div><label style={fieldLabel}>ID аккаунта / кабинета (опц.)</label><input className="input-sm" style={fullCtl} value={account} onChange={e => setAccount(e.target.value)} placeholder="напр. account_id"/></div>
        <div style={{ gridColumn: "1 / -1" }}><label style={fieldLabel}>API-ключ / токен</label><input className="input-sm" style={fullCtl} value={apiKey} onChange={e => setApiKey(e.target.value)} placeholder="вставьте ключ/токен"/></div>
        <div style={{ gridColumn: "1 / -1" }}><label style={fieldLabel}>Заметка (опц.)</label><input className="input-sm" style={fullCtl} value={note} onChange={e => setNote(e.target.value)}/></div>
      </div>
      <div style={{ marginTop: 14, display: "flex", gap: 8 }}>
        <button className="btn btn-sm btn-primary" onClick={() => onSave({ name, account, api_key: apiKey, note })}>{submitLabel}</button>
        <button className="btn btn-sm" onClick={onCancel}>Отмена</button>
      </div>
    </div>
  );
}

function AccountCard({ acc }: { acc: MarketingAccount }) {
  const qc = useQueryClient();
  const [reauth, setReauth] = useState(false);
  const [showGoals, setShowGoals] = useState(false);
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(acc.title || "");
  const syncMut = useMutation({
    mutationFn: () => syncMarketing({ days: 30, accountId: acc.id }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["marketing/accounts"] }),
  });
  const renameMut = useMutation({
    mutationFn: () => updateMarketingAccount(acc.id, { title: name.trim() }),
    onSuccess: () => { setEditing(false); qc.invalidateQueries({ queryKey: ["marketing/accounts"] }); },
  });
  const delMut = useMutation({
    mutationFn: () => deleteMarketingAccount(acc.id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["marketing/accounts"] }),
  });

  return (
    <div className="card" style={cardStyle}>
      <div style={{ display: "flex", gap: 14, alignItems: "flex-start" }}>
        <BrandLogo id="yandex_direct" size={44}/>
        <div style={{ flex: 1, minWidth: 0 }}>
          {editing ? (
            <div style={{ display: "flex", gap: 8, alignItems: "center", maxWidth: 420 }}>
              <input className="input-sm" style={{ ...fullCtl, fontWeight: 600 }} value={name} autoFocus
                     onChange={e => setName(e.target.value)}
                     onKeyDown={e => { if (e.key === "Enter") renameMut.mutate(); if (e.key === "Escape") setEditing(false); }}/>
              <button className="btn btn-sm btn-primary" style={{ flex: "0 0 auto" }} disabled={renameMut.isPending} onClick={() => renameMut.mutate()}>
                {renameMut.isPending ? "…" : "ОК"}
              </button>
              <button className="btn btn-sm" style={{ flex: "0 0 auto" }} onClick={() => { setName(acc.title || ""); setEditing(false); }}>Отмена</button>
            </div>
          ) : (
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <span style={{ fontWeight: 700, fontSize: 16, cursor: "text" }}
                    title="Двойной клик — переименовать"
                    onDoubleClick={() => { setName(acc.title || ""); setEditing(true); }}>
                {acc.title || acc.login}
              </span>
              <Pill text="Яндекс.Директ" kind="muted"/>
              {acc.last_sync_error ? <Pill text="ошибка синка" kind="err"/> : acc.last_sync_at ? <Pill text="синхронизирован" kind="ok"/> : <Pill text="не синхронизирован" kind="warn"/>}
            </div>
          )}
          <div style={{ ...muted, marginTop: 5 }}>
            login {acc.login}
            {acc.last_sync_at && <> · синк {new Date(acc.last_sync_at).toLocaleString("ru")}</>}
            {acc.metrika_counter_id && <> · счётчик {acc.metrika_counter_id}</>}
          </div>
        </div>
      </div>

      {(acc.last_sync_error || syncMut.data?.error) && (
        <div style={{ marginTop: 10, fontSize: 12.5, color: "var(--brick)" }}>
          {syncMut.data?.error ? `Синк с ошибками: ${syncMut.data.error}` : `Ошибка синка: ${acc.last_sync_error}`}
        </div>
      )}

      <div style={{ display: "flex", gap: 8, marginTop: 14, paddingTop: 14, borderTop: "1px solid var(--line)", flexWrap: "wrap" }}>
        <button className="btn btn-sm btn-primary" disabled={syncMut.isPending} onClick={() => syncMut.mutate()}>
          {syncMut.isPending ? "Синхронизирую…" : "Синхронизировать"}
        </button>
        <button className="btn btn-sm" onClick={() => setShowGoals(s => !s)}>Цели и атрибуция</button>
        <button className="btn btn-sm" onClick={() => setReauth(r => !r)}>Переподключить</button>
        <button className="btn btn-sm" style={{ marginLeft: "auto", color: "#b23b30" }} onClick={() => {
          if (window.confirm(`Отключить кабинет «${acc.title || acc.login}»? Токены удалятся, статистика останется.`)) delMut.mutate();
        }}>Отключить</button>
      </div>

      {reauth && (
        <div style={{ marginTop: 16, borderTop: "1px solid var(--line)", paddingTop: 16 }}>
          <div style={{ ...muted, marginBottom: 12 }}>
            Переподключение обновит доступ (например, после выдачи прав Метрики). Логин подставлен — получите новый код и нажмите «Подключить».
          </div>
          <ConnectForm accountId={acc.id} defaultLogin={acc.login} defaultTitle={acc.title}
                       onDone={() => { setReauth(false); qc.invalidateQueries({ queryKey: ["marketing/accounts"] }); }}
                       onCancel={() => setReauth(false)}/>
        </div>
      )}

      {showGoals && (
        <div style={{ marginTop: 16, borderTop: "1px solid var(--line)", paddingTop: 16 }}>
          <GoalsConfig accountId={acc.id}/>
        </div>
      )}
    </div>
  );
}

function ConnectForm({ accountId, defaultLogin, defaultTitle, onDone, onCancel }: {
  accountId?: number; defaultLogin?: string; defaultTitle?: string;
  onDone: () => void; onCancel?: () => void;
}) {
  const oauthQ = useQuery({ queryKey: ["marketing/oauth-status"], queryFn: fetchMarketingOAuthStatus });
  const [code, setCode] = useState("");
  const [login, setLogin] = useState(defaultLogin ?? "");
  const [title, setTitle] = useState(defaultTitle ?? "");
  const mut = useMutation({
    mutationFn: () => connectMarketing({ verification_code: code, login, title, account_id: accountId }),
    onSuccess: onDone,
  });
  const oauth = oauthQ.data;

  if (oauthQ.isLoading) return <div style={{ display: "flex", justifyContent: "center", padding: "28px 12px" }}><Spinner label="Загрузка…"/></div>;
  if (!oauth?.configured) {
    return (
      <div style={{ fontSize: 13, color: "var(--brick)" }}>
        OAuth-приложение не настроено: {oauth?.error || "нет credentials в .env"}.
      </div>
    );
  }

  return (
    <div>
      <ol style={{ paddingLeft: 18, lineHeight: 1.8, fontSize: 13, margin: "0 0 14px" }}>
        <li>
          <a href={oauth.authorize_url || "#"} target="_blank" rel="noopener" className="btn btn-sm btn-primary" style={{ marginRight: 8 }}>
            Открыть авторизацию <Icon name="external" size={11}/>
          </a>
          Подтвердите доступ под Яндекс-аккаунтом кабинета.
        </li>
        <li>Скопируйте короткий код (verification_code).</li>
        <li>Заполните и нажмите «Подключить».</li>
      </ol>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <div>
          <label style={fieldLabel}>Verification code</label>
          <input className="input-sm" style={{ ...fullCtl, fontFamily: "var(--mono)" }} value={code} onChange={e => setCode(e.target.value)} placeholder="напр. 1234567"/>
        </div>
        <div>
          <label style={fieldLabel}>Login кабинета (Client-Login)</label>
          <input className="input-sm" style={fullCtl} value={login} onChange={e => setLogin(e.target.value)} placeholder="логин"/>
        </div>
        <div style={{ gridColumn: "1 / -1" }}>
          <label style={fieldLabel}>Название (для UI)</label>
          <input className="input-sm" style={fullCtl} value={title} onChange={e => setTitle(e.target.value)} placeholder="напр. Беседки — основной"/>
        </div>
      </div>
      <div style={{ marginTop: 14, display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
        <button className="btn btn-sm btn-primary" disabled={!code.trim() || !login.trim() || mut.isPending} onClick={() => mut.mutate()}>
          {mut.isPending ? "Подключаю…" : "Подключить"}
        </button>
        {onCancel && <button className="btn btn-sm" onClick={onCancel}>Отмена</button>}
        {mut.isError && <span style={{ color: "var(--brick)", fontSize: 12 }}>{(mut.error as Error).message}</span>}
      </div>
    </div>
  );
}

function GoalsConfig({ accountId }: { accountId: number }) {
  const qc = useQueryClient();
  const goalsQ = useQuery({ queryKey: ["marketing/goals", accountId], queryFn: () => fetchMarketingGoals(accountId) });
  const countersQ = useQuery({ queryKey: ["marketing/metrika-counters", accountId], queryFn: () => fetchMarketingMetrikaCounters(accountId), enabled: false });
  const [selected, setSelected] = useState<number[] | null>(null);
  const [attribution, setAttribution] = useState<string>("AUTO");
  const [counterPick, setCounterPick] = useState<number | null>(null);

  const g = goalsQ.data;
  const sel = selected ?? g?.selected ?? [];
  const attr = selected === null ? (g?.attribution_model ?? "AUTO") : attribution;

  const importMut = useMutation({
    mutationFn: (cid: number) => importMarketingGoals(cid, accountId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["marketing/goals", accountId] }),
  });
  const saveMut = useMutation({
    mutationFn: () => saveMarketingGoals({ goal_ids: sel, attribution_model: attr }, accountId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["marketing/goals", accountId] }),
  });
  const toggle = (id: number) => {
    const cur = selected ?? g?.selected ?? [];
    setSelected(cur.includes(id) ? cur.filter(x => x !== id) : [...cur, id]);
  };

  if (goalsQ.isLoading) return <div style={{ display: "flex", justifyContent: "center", padding: "28px 12px" }}><Spinner label="Загрузка…"/></div>;

  return (
    <div>
      <div style={sectionTitle}>Цели Метрики и атрибуция</div>
      <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", marginBottom: 12 }}>
        <span style={{ fontSize: 12.5, color: "var(--ink-3)" }}>Счётчик: <strong style={{ color: "var(--ink)" }}>{g?.counter_id || "не привязан"}</strong></span>
        <button className="btn btn-sm" onClick={() => countersQ.refetch()}>{countersQ.isFetching ? "Загружаю…" : "Выбрать счётчик"}</button>
        {countersQ.data && countersQ.data.length > 0 && (
          <>
            <select className="input-sm" style={{ minWidth: 200 }} value={counterPick ?? ""} onChange={e => setCounterPick(Number(e.target.value))}>
              <option value="">— счётчик —</option>
              {countersQ.data.map(c => <option key={c.counter_id} value={c.counter_id}>{c.counter_id} · {c.name || c.site}</option>)}
            </select>
            <button className="btn btn-sm btn-primary" disabled={!counterPick || importMut.isPending} onClick={() => counterPick && importMut.mutate(counterPick)}>
              {importMut.isPending ? "Импорт…" : "Импортировать цели"}
            </button>
          </>
        )}
        {countersQ.isError && <span style={{ color: "var(--brick)", fontSize: 12 }}>{(countersQ.error as Error).message}</span>}
      </div>

      {g?.available && g.available.length > 0 ? (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 12 }}>
          {g.available.map(goal => (
            <button key={goal.goal_id} style={chipStyle(sel.includes(goal.goal_id))} onClick={() => toggle(goal.goal_id)}>
              {sel.includes(goal.goal_id) ? "✓ " : ""}{goal.name || `Цель #${goal.goal_id}`}
            </button>
          ))}
        </div>
      ) : (
        <div style={{ ...muted, marginBottom: 12 }}>
          Целей нет. Выберите счётчик и импортируйте. Пустой выбор = конверсии по всем целям.
        </div>
      )}

      <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
        <span style={{ fontSize: 12, color: "var(--ink-3)" }}>Модель атрибуции:</span>
        <select className="input-sm" style={{ minWidth: 120 }} value={attr} onChange={e => { setSelected(sel); setAttribution(e.target.value); }}>
          {(g?.attribution_options ?? ["AUTO", "LC", "LSC", "FC", "LYDIRECT"]).map(m => <option key={m} value={m}>{m}</option>)}
        </select>
        <button className="btn btn-sm btn-primary" disabled={saveMut.isPending} onClick={() => saveMut.mutate()}>
          {saveMut.isPending ? "Сохраняю…" : "Сохранить"}
        </button>
        {saveMut.isSuccess && <span style={{ fontSize: 12, color: GREEN }}>Сохранено</span>}
        {saveMut.isError && <span style={{ color: "var(--brick)", fontSize: 12 }}>{(saveMut.error as Error).message}</span>}
      </div>
    </div>
  );
}

// ============ CRM-системы ============

function CrmTab() {
  const qc = useQueryClient();
  const statusQ = useQuery({ queryKey: ["marketing/crm-status"], queryFn: fetchCrmStatus });
  const typesQ = useQuery({ queryKey: ["marketing/crm-types"], queryFn: fetchCrmTypes });
  const connsQ = useQuery({ queryKey: ["marketing/crm-connections"], queryFn: fetchCrmConnections });
  const [adding, setAdding] = useState<string | null>(null);

  const types = typesQ.data?.types ?? [];
  const list = connsQ.data?.connections ?? [];
  const labelFor = (t: string) => types.find(x => x.type === t)?.label ?? t;
  const wiredFor = (t: string) => types.find(x => x.type === t)?.wired ?? false;
  const saveMut = useMutation({
    mutationFn: (next: CrmConnection[]) => saveCrmConnections(next),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["marketing/crm-connections"] }),
  });
  const overrideQ = useQuery({ queryKey: ["marketing/amo-override"], queryFn: fetchAmoOverride });
  const [amoEdit, setAmoEdit] = useState(false);
  const [amoDomain, setAmoDomain] = useState("");
  const [amoToken, setAmoToken] = useState("");
  const overrideMut = useMutation({
    mutationFn: (o: { mode: string; domain: string; token: string }) => saveAmoOverride(o),
    onSuccess: () => { setAmoEdit(false); qc.invalidateQueries({ queryKey: ["marketing/amo-override"] }); qc.invalidateQueries({ queryKey: ["marketing/crm-status"] }); },
  });

  if (statusQ.isLoading || connsQ.isLoading) return <div style={{ display: "flex", justifyContent: "center", padding: "28px 12px" }}><Spinner size="lg" label="Загрузка…"/></div>;
  const amo = statusQ.data?.amocrm;
  const ovr = overrideQ.data;
  const amoMode = ovr?.mode ?? "kb";

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <p style={{ ...muted, margin: 0 }}>
        Подключения CRM и сквозной аналитики. AmoCRM — основная интеграция KB, используется для воронки.
        Остальные сохраняются, глубокая интеграция подключается поэтапно.
      </p>

      {/* AmoCRM — основная */}
      <div className="card" style={cardStyle}>
        <div style={{ display: "flex", gap: 14, alignItems: "flex-start" }}>
          <BrandLogo id="amocrm" label="AmoCRM" size={44}/>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <span style={{ fontWeight: 700, fontSize: 15 }}>AmoCRM</span>
              <Pill text={amo?.connected ? "Подключено" : "Ошибка"} kind={amo?.connected ? "ok" : "err"}/>
              <Pill text="основная" kind="muted"/>
            </div>
            <div style={{ ...muted, marginTop: 4 }}>
              {amo?.domain || "—"}{amo?.connected && <> · воронок: {amo.pipelines}</>}
              {amo?.error && <span style={{ color: "var(--brick)" }}> · {amo.error}</span>}
            </div>
            <div style={{ ...muted, marginTop: 3 }}>
              {amoMode === "custom" ? "Свои домен+токен для маркетинга (поверх KB)."
                : amoMode === "off" ? "Отключено для маркетинга — воронка по сделкам не считается."
                : "Интеграция KB (через .env). Используется для расчёта воронки и сделок."}
            </div>
          </div>
        </div>

        {amoEdit && (
          <div style={{ marginTop: 14, borderTop: "1px solid var(--line)", paddingTop: 14 }}>
            <div style={{ ...muted, marginBottom: 10 }}>
              Переподключить AmoCRM для маркетинга своими реквизитами (глобальную KB-интеграцию не трогает).
              Пусто — вернуться к KB (.env).
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <div><label style={fieldLabel}>Домен (напр. company.amocrm.ru)</label>
                <input className="input-sm" style={fullCtl} value={amoDomain} onChange={e => setAmoDomain(e.target.value)} placeholder={amo?.domain || "company.amocrm.ru"}/></div>
              <div><label style={fieldLabel}>Долгоживущий токен</label>
                <input className="input-sm" style={fullCtl} value={amoToken} onChange={e => setAmoToken(e.target.value)} placeholder="вставьте токен"/></div>
            </div>
            <div style={{ marginTop: 12, display: "flex", gap: 8 }}>
              <button className="btn btn-sm btn-primary" disabled={overrideMut.isPending}
                      onClick={() => overrideMut.mutate({ mode: amoDomain.trim() && amoToken.trim() ? "custom" : "kb", domain: amoDomain.trim(), token: amoToken.trim() })}>
                {overrideMut.isPending ? "Сохраняю…" : "Подключить"}
              </button>
              <button className="btn btn-sm" onClick={() => setAmoEdit(false)}>Отмена</button>
              {overrideMut.isError && <span style={{ fontSize: 12, color: "var(--brick)" }}>{(overrideMut.error as Error).message}</span>}
            </div>
          </div>
        )}

        <div style={{ display: "flex", gap: 8, marginTop: 14, paddingTop: 14, borderTop: "1px solid var(--line)", flexWrap: "wrap" }}>
          <button className="btn btn-sm" disabled={statusQ.isFetching} onClick={() => statusQ.refetch()}>
            {statusQ.isFetching ? "Проверяю…" : "Проверить связь"}
          </button>
          <button className="btn btn-sm" onClick={() => { setAmoDomain(amoMode === "custom" ? (ovr?.domain || "") : ""); setAmoToken(""); setAmoEdit(e => !e); }}>
            Переподключить
          </button>
          {amoMode === "off" ? (
            <button className="btn btn-sm" style={{ marginLeft: "auto" }} disabled={overrideMut.isPending}
                    onClick={() => overrideMut.mutate({ mode: "kb", domain: "", token: "" })}>Включить (KB)</button>
          ) : (
            <button className="btn btn-sm" style={{ marginLeft: "auto", color: "#b23b30" }} disabled={overrideMut.isPending}
                    onClick={() => { if (window.confirm("Отключить AmoCRM для маркетинга? Воронка по сделкам перестанет считаться (на остальной KB не влияет).")) overrideMut.mutate({ mode: "off", domain: "", token: "" }); }}>
              Отключить
            </button>
          )}
        </div>
      </div>

      {list.map(c => (
        <CrmConnCard key={c.id} conn={c} label={labelFor(c.type)} wired={wiredFor(c.type)}
                     onSave={upd => saveMut.mutate(list.map(x => x.id === c.id ? upd : x))}
                     onRemove={() => saveMut.mutate(list.filter(x => x.id !== c.id))}/>
      ))}

      {adding === null && (
        <div><button className="btn btn-sm btn-primary" onClick={() => setAdding("pick")}>+ Добавить CRM</button></div>
      )}

      {adding === "pick" && (
        <div className="card" style={cardStyle}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
            <div style={{ ...sectionTitle, margin: 0 }}>Какую CRM подключить?</div>
            <button className="btn btn-sm" onClick={() => setAdding(null)}>Отмена</button>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))", gap: 10 }}>
            {types.filter(t => t.type !== "amocrm").map(t => (
              <button key={t.type} onClick={() => setAdding(t.type)}
                      style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 14px", borderRadius: 12,
                               border: "1px solid var(--line)", background: "var(--card)", cursor: "pointer", textAlign: "left" }}
                      onMouseEnter={e => { e.currentTarget.style.borderColor = GREEN; e.currentTarget.style.boxShadow = "0 2px 8px rgba(79,174,78,0.15)"; }}
                      onMouseLeave={e => { e.currentTarget.style.borderColor = "var(--line)"; e.currentTarget.style.boxShadow = "none"; }}>
                <BrandLogo id={t.type} label={t.label} size={34}/>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: 13, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{t.label}</div>
                  <div style={{ fontSize: 10.5, color: t.wired ? "#1f7a3f" : "var(--mute)" }}>{t.wired ? "интегрирован" : "поэтапно"}</div>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {adding != null && adding !== "pick" && (
        <CrmConnForm label={labelFor(adding)} wired={wiredFor(adding)}
                     onSave={f => { saveMut.mutate([...list, { id: "", type: adding, ...f } as CrmConnection]); setAdding(null); }}
                     onCancel={() => setAdding(null)}/>
      )}

      {saveMut.isError && <span style={{ fontSize: 12, color: "var(--brick)" }}>{(saveMut.error as Error).message}</span>}
    </div>
  );
}

function CrmConnCard({ conn, label, wired, onSave, onRemove }: {
  conn: CrmConnection; label: string; wired: boolean;
  onSave: (c: CrmConnection) => void; onRemove: () => void;
}) {
  const [editing, setEditing] = useState(false);
  if (editing) {
    return (
      <CrmConnForm label={label} wired={wired} submitLabel="Сохранить"
                   initial={{ name: conn.name, domain: conn.domain, api_key: conn.api_key, note: conn.note }}
                   onSave={f => { onSave({ ...conn, ...f }); setEditing(false); }} onCancel={() => setEditing(false)}/>
    );
  }
  return (
    <div className="card" style={cardStyle}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <div style={{ display: "flex", gap: 12, alignItems: "center", minWidth: 0 }}>
          <BrandLogo id={conn.type} label={label}/>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontWeight: 700, fontSize: 15, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              {conn.name || label}
              <Pill text={label} kind="muted"/>
              {!wired && <Pill text="поэтапно" kind="warn"/>}
            </div>
            <div style={{ ...muted, marginTop: 3 }}>
              {conn.domain && <>{conn.domain} · </>}ключ {conn.api_key || "—"}{conn.note && <> · {conn.note}</>}
            </div>
          </div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button className="btn btn-sm" onClick={() => setEditing(true)}>Изменить</button>
          <button className="btn btn-sm" onClick={onRemove}>Удалить</button>
        </div>
      </div>
    </div>
  );
}

function CrmConnForm({ label, wired, initial, submitLabel = "Добавить", onSave, onCancel }: {
  label: string; wired: boolean;
  initial?: { name: string; domain: string; api_key: string; note: string };
  submitLabel?: string;
  onSave: (f: { name: string; domain: string; api_key: string; note: string }) => void; onCancel: () => void;
}) {
  const [name, setName] = useState(initial?.name ?? "");
  const [domain, setDomain] = useState(initial?.domain ?? "");
  const [apiKey, setApiKey] = useState(initial?.api_key ?? "");
  const [note, setNote] = useState(initial?.note ?? "");
  return (
    <div className="card" style={cardStyle}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
        <BrandLogo id="other" label={label} size={34}/>
        <div style={{ ...sectionTitle, margin: 0 }}>{submitLabel === "Сохранить" ? "Изменить" : "Добавить"} {label}</div>
      </div>
      {!wired && <div style={{ ...muted, marginBottom: 10 }}>Конфиг сохранится. Глубокая интеграция — поэтапно.</div>}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <div><label style={fieldLabel}>Название</label><input className="input-sm" style={fullCtl} value={name} onChange={e => setName(e.target.value)} placeholder={label}/></div>
        <div><label style={fieldLabel}>Домен / URL (опц.)</label><input className="input-sm" style={fullCtl} value={domain} onChange={e => setDomain(e.target.value)} placeholder="напр. account.roistat.com"/></div>
        <div style={{ gridColumn: "1 / -1" }}><label style={fieldLabel}>API-ключ / токен</label><input className="input-sm" style={fullCtl} value={apiKey} onChange={e => setApiKey(e.target.value)} placeholder="вставьте ключ"/></div>
        <div style={{ gridColumn: "1 / -1" }}><label style={fieldLabel}>Заметка (опц.)</label><input className="input-sm" style={fullCtl} value={note} onChange={e => setNote(e.target.value)}/></div>
      </div>
      <div style={{ marginTop: 14, display: "flex", gap: 8 }}>
        <button className="btn btn-sm btn-primary" onClick={() => onSave({ name, domain, api_key: apiKey, note })}>{submitLabel}</button>
        <button className="btn btn-sm" onClick={onCancel}>Отмена</button>
      </div>
    </div>
  );
}

// ============ Сегменты воронки ============

type LocalRule = { field_id: number | null; values: string };
type LocalSeg = { uid: number; name: string; match: "any" | "all"; rules: LocalRule[]; stageIds: Set<number> };

function RuleRow({ rule, fields, onChange, onRemove }: {
  rule: LocalRule; fields: AmoCustomField[];
  onChange: (patch: Partial<LocalRule>) => void; onRemove: () => void;
}) {
  const [showVals, setShowVals] = useState(false);
  const valsQ = useQuery({
    queryKey: ["marketing/field-values", rule.field_id],
    queryFn: () => fetchAmoFieldValues(rule.field_id as number),
    enabled: showVals && rule.field_id != null,
  });
  const curValues = rule.values.split(",").map(s => s.trim()).filter(Boolean);
  const addValue = (v: string) => {
    if (!curValues.includes(v)) onChange({ values: [...curValues, v].join(", ") });
  };

  return (
    <div>
      <div style={{ display: "grid", gridTemplateColumns: "minmax(150px, 230px) 14px 1fr auto 34px", gap: 8, alignItems: "center" }}>
        <select className="input-sm" style={fullCtl} value={rule.field_id ?? ""}
                onChange={e => onChange({ field_id: e.target.value ? Number(e.target.value) : null })}>
          <option value="">— поле —</option>
          {fields.map(f => <option key={f.id} value={f.id}>{f.name}{f.code ? ` (${f.code})` : ""}</option>)}
        </select>
        <span style={{ fontSize: 12, color: "var(--mute)", textAlign: "center" }}>=</span>
        <input className="input-sm" style={fullCtl} value={rule.values}
               onChange={e => onChange({ values: e.target.value })}
               placeholder="значения через запятую; пусто = любое непустое"/>
        <button className="btn btn-sm" style={{ flex: "0 0 auto" }} disabled={rule.field_id == null}
                onClick={() => setShowVals(s => !s)}>значения из CRM</button>
        <button className="btn btn-sm" style={{ padding: "6px 0", justifyContent: "center" }} onClick={onRemove}>×</button>
      </div>
      {showVals && rule.field_id != null && (
        <div style={{ marginTop: 6 }}>
          {valsQ.isLoading ? <span style={muted}>Загружаю значения…</span>
            : valsQ.data?.error ? <span style={{ ...muted, color: "var(--brick)" }}>{valsQ.data.error}</span>
            : !valsQ.data?.values?.length ? <span style={muted}>Значений не найдено за 60 дней.</span>
            : (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {valsQ.data.values.map(v => (
                  <button key={v.value} style={chipStyle(curValues.includes(v.value))}
                          onClick={() => addValue(v.value)} title={`${v.count} сделок`}>
                    {v.value} <span style={{ opacity: 0.7 }}>· {v.count}</span>
                  </button>
                ))}
              </div>
            )}
        </div>
      )}
    </div>
  );
}

function SuccessStagesTab() {
  const qc = useQueryClient();
  const stagesQ = useQuery({ queryKey: ["marketing/amo-stages"], queryFn: fetchAmoStages });
  const curQ = useQuery({ queryKey: ["marketing/success-stages"], queryFn: fetchSuccessStages });
  const [sel, setSel] = useState<Set<number> | null>(null);
  const selected = sel ?? new Set<number>(curQ.data?.stage_ids ?? []);
  const saveMut = useMutation({
    mutationFn: () => saveSuccessStages([...selected]),
    onSuccess: (d) => {
      setSel(new Set(d.stage_ids));
      qc.invalidateQueries({ queryKey: ["marketing/success-stages"] });
      qc.invalidateQueries({ queryKey: ["marketing/success-deals"] });
    },
  });
  const toggle = (id: number) => { const s = new Set(selected); s.has(id) ? s.delete(id) : s.add(id); setSel(s); };
  const stages = stagesQ.data?.stages ?? [];
  const byPipeline = new Map<string, AmoStage[]>();
  for (const s of stages) { const a = byPipeline.get(s.pipeline_name) ?? []; a.push(s); byPipeline.set(s.pipeline_name, a); }
  const chosen = stages.filter(s => selected.has(s.stage_id));
  // По умолчанию воронки свёрнуты; авто-разворачиваем те, где есть выбранные этапы.
  const [expandedOverride, setExpandedOverride] = useState<Record<string, boolean>>({});
  const hasSel = (sts: AmoStage[]) => sts.some(s => selected.has(s.stage_id));
  const isExp = (pname: string, sts: AmoStage[]) => expandedOverride[pname] ?? hasSel(sts);
  const toggleExp = (pname: string, sts: AmoStage[]) => setExpandedOverride(o => ({ ...o, [pname]: !isExp(pname, sts) }));

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <p style={{ ...muted, margin: 0 }}>
        Отметь этапы = «Успешная сделка» (договор). Можно несколько из разных воронок — на дашборде
        раздел «Успешные сделки» покажет их сгруппированно по этапам.
      </p>
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        <BrandLogo id="amocrm" label="AmoCRM" size={22}/>
        <span style={{ fontSize: 12.5, color: "var(--ink-2)" }}>Этапы из <strong>AmoCRM</strong> · воронок: {byPipeline.size}</span>
      </div>

      {/* Выбранные этапы — чипами, видно при свёрнутых воронках */}
      {chosen.length > 0 && (
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {chosen.map(s => (
            <span key={s.stage_id} style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, fontWeight: 600,
                          padding: "4px 8px 4px 10px", borderRadius: 14, background: "#eaf6ee", color: "#1f7a3f" }}>
              {s.name}
              <button onClick={() => toggle(s.stage_id)} title="Убрать"
                      style={{ border: "none", background: "transparent", cursor: "pointer", color: "#1f7a3f", fontSize: 13, lineHeight: 1, padding: 0 }}>×</button>
            </span>
          ))}
        </div>
      )}

      {stagesQ.data?.error && <div style={{ color: "var(--brick)", fontSize: 12.5 }}>Этапы CRM: {stagesQ.data.error}</div>}
      {stagesQ.isLoading ? <div style={{ display: "flex", justifyContent: "center", padding: "28px 12px" }}><Spinner label="Загрузка…"/></div> : stages.length === 0 ? (
        <div style={{ ...muted }}>Этапы не загрузились — проверь подключение CRM.</div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {[...byPipeline.entries()].map(([pname, sts]) => {
            const exp = isExp(pname, sts);
            const cnt = sts.filter(s => selected.has(s.stage_id)).length;
            return (
              <div key={pname} className="card" style={{ ...cardStyle, padding: 0, overflow: "hidden" }}>
                <button onClick={() => toggleExp(pname, sts)}
                        style={{ width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10,
                                 padding: "12px 14px", border: "none", background: "transparent", cursor: "pointer", textAlign: "left" }}>
                  <span style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
                    <span style={{ color: "var(--mute)", fontSize: 12, width: 12 }}>{exp ? "▾" : "▸"}</span>
                    <span style={{ fontWeight: 700, fontSize: 13 }}>{pname}</span>
                    {cnt > 0 && <span style={{ fontSize: 11, fontWeight: 600, padding: "1px 7px", borderRadius: 10, background: "#eaf6ee", color: "#1f7a3f" }}>{cnt}</span>}
                  </span>
                  <span style={{ ...muted, fontSize: 11 }}>{sts.length} этапов</span>
                </button>
                {exp && (
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6, padding: "0 14px 14px", borderTop: "1px solid var(--line)", paddingTop: 12 }}>
                    {sts.map(s => (
                      <label key={s.stage_id} style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 12.5, cursor: "pointer" }}>
                        <input type="checkbox" checked={selected.has(s.stage_id)} onChange={() => toggle(s.stage_id)}/>
                        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{s.name}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      <div style={{ display: "flex", gap: 8, alignItems: "center", position: "sticky", bottom: 0, background: "var(--paper, #f4f4f5)", padding: "8px 0" }}>
        <button className="btn btn-sm btn-primary" disabled={saveMut.isPending} onClick={() => saveMut.mutate()}>
          {saveMut.isPending ? "Сохраняю…" : "Сохранить"}
        </button>
        {saveMut.isSuccess && <span style={{ fontSize: 12, color: GREEN }}>Сохранено</span>}
        <span style={muted}>Выбрано этапов: {selected.size}</span>
      </div>
    </div>
  );
}

function FunnelConfigTab() {
  const qc = useQueryClient();
  const cfgQ = useQuery({ queryKey: ["marketing/crm-config"], queryFn: fetchCrmConfig });
  const fieldsQ = useQuery({ queryKey: ["marketing/amo-fields"], queryFn: fetchAmoCustomFields });
  const stagesQ = useQuery({ queryKey: ["marketing/amo-stages"], queryFn: fetchAmoStages });
  const callsModeQ = useQuery({ queryKey: ["marketing/calls-mode"], queryFn: fetchCallsMode });
  const callsModeMut = useMutation({
    mutationFn: (m: string) => saveCallsMode(m),
    onSuccess: d => { qc.setQueryData(["marketing/calls-mode"], d); qc.invalidateQueries({ queryKey: ["marketing/campaign-funnel"] }); },
  });
  const callsMode = callsModeQ.data?.mode ?? "all";

  const [segments, setSegments] = useState<LocalSeg[] | null>(null);
  // Свёрнутые сегменты (UI-only, по uid). По умолчанию все свёрнуты — компактно.
  const [collapsed, setCollapsed] = useState<Set<number> | null>(null);
  const cfg = cfgQ.data;
  const segs: LocalSeg[] = segments ?? (cfg?.segments ?? []).map((s, i) => ({
    uid: i,
    name: s.name,
    match: s.match === "all" ? "all" : "any",
    rules: (s.utm_rules ?? []).map(r => ({ field_id: r.field_id, values: (r.values ?? []).join(", ") })),
    stageIds: new Set<number>(s.stage_ids ?? []),
  }));
  const effCollapsed = collapsed ?? new Set<number>(segs.map(s => s.uid));
  const toggleCollapse = (uid: number) => {
    const n = new Set(effCollapsed);
    n.has(uid) ? n.delete(uid) : n.add(uid);
    setCollapsed(n);
  };

  const saveMut = useMutation({
    mutationFn: () => {
      const fields = fieldsQ.data?.fields ?? [];
      return saveCrmConfig({
        segments: segs.map(s => ({
          name: s.name,
          match: s.match,
          utm_rules: s.rules.filter(r => r.field_id != null).map(r => ({
            field_id: r.field_id,
            field_name: fields.find(f => f.id === r.field_id)?.name ?? null,
            values: r.values.split(",").map(v => v.trim()).filter(Boolean),
          })),
          stage_ids: [...s.stageIds],
        })),
      });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["marketing/crm-config"] }),
  });

  const update = (next: LocalSeg[]) => setSegments(next);
  const patchSeg = (i: number, patch: Partial<LocalSeg>) => update(segs.map((s, j) => j === i ? { ...s, ...patch } : s));
  const addSeg = () => {
    const uid = segs.reduce((m, s) => Math.max(m, s.uid), -1) + 1;
    update([...segs, { uid, name: "", match: "any", rules: [{ field_id: null, values: "" }], stageIds: new Set() }]);
    const n = new Set(effCollapsed); n.delete(uid); setCollapsed(n);  // новый — развёрнут
  };
  const removeSeg = (i: number) => update(segs.filter((_, j) => j !== i));

  if (cfgQ.isLoading) return <div style={{ display: "flex", justifyContent: "center", padding: "28px 12px" }}><Spinner size="lg" label="Загрузка…"/></div>;

  const fields = fieldsQ.data?.fields ?? [];
  const stages = stagesQ.data?.stages ?? [];
  const byPipeline = new Map<string, AmoStage[]>();
  for (const s of stages) { const a = byPipeline.get(s.pipeline_name) ?? []; a.push(s); byPipeline.set(s.pipeline_name, a); }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <p style={{ ...muted, margin: 0 }}>
        Сегмент = своя UTM-разметка + свои этапы. Можно сделать несколько (например «Я.Директ»
        с одними этапами и «Авито» с другими) — каждый посчитается отдельно.
      </p>

      {fieldsQ.data?.error && <div style={{ color: "var(--brick)", fontSize: 12.5 }}>Поля Amo: {fieldsQ.data.error}</div>}
      {stagesQ.data?.error && <div style={{ color: "var(--brick)", fontSize: 12.5 }}>Этапы Amo: {stagesQ.data.error}</div>}

      {/* Метрики из Amo: режим подсчёта звонков. Выручка/ROMI/окупаемость — столбцы в таблице кампаний. */}
      <div className="card" style={cardStyle}>
        <div style={{ ...sectionTitle, marginBottom: 8 }}>Звонки из Amo</div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button className={`btn btn-sm ${callsMode === "all" ? "btn-primary" : ""}`} disabled={callsModeMut.isPending}
                  onClick={() => callsModeMut.mutate("all")}>Все звонки из Amo</button>
          <button className={`btn btn-sm ${callsMode === "telephony" ? "btn-primary" : ""}`} disabled={callsModeMut.isPending}
                  onClick={() => callsModeMut.mutate("telephony")}>Только из телефонии</button>
        </div>
        <div style={{ fontSize: 12, color: "var(--mute)", marginTop: 8, lineHeight: 1.5 }}>
          «Все» — считаются все звонки в Amo (включая подгруженные/импортированные). «Только телефония» — лишь звонки
          из подключённой телефонии (есть длительность/источник). Колонки <strong>Выручка, ROMI, Доля маркетинга, Окупаемость</strong>
          {" "}считаются по выручке из Amo ÷ расход Директа — включи их в таблице кампаний (шестерёнка → столбцы).
        </div>
      </div>

      {segs.length === 0 && (
        <div className="card" style={{ ...cardStyle, textAlign: "center", color: "var(--mute)" }}>Сегментов нет — добавьте первый.</div>
      )}

      {segs.map((seg, si) => {
        const isCollapsed = effCollapsed.has(seg.uid);
        return (
        <div key={seg.uid} className="card" style={cardStyle}>
          <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: isCollapsed ? 0 : 16 }}>
            <button onClick={() => toggleCollapse(seg.uid)} title={isCollapsed ? "Развернуть" : "Свернуть"}
                    style={{ flex: "0 0 auto", width: 26, height: 26, border: "1px solid var(--line)", borderRadius: 6,
                             background: "var(--card)", cursor: "pointer", color: "var(--ink-2)", fontSize: 12 }}>
              {isCollapsed ? "▸" : "▾"}
            </button>
            {isCollapsed ? (
              <div style={{ flex: 1, display: "flex", alignItems: "baseline", gap: 8, cursor: "pointer", minWidth: 0 }}
                   onClick={() => toggleCollapse(seg.uid)}>
                <span style={{ fontWeight: 700, fontSize: 15, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {seg.name || "Без названия"}
                </span>
                <span style={{ fontSize: 12, color: "var(--mute)", whiteSpace: "nowrap" }}>
                  {seg.rules.filter(r => r.field_id != null).length} полей · {seg.stageIds.size} этапов
                </span>
              </div>
            ) : (
              <input className="input-sm" style={{ ...fullCtl, fontWeight: 600 }} value={seg.name}
                     onChange={e => patchSeg(si, { name: e.target.value })} placeholder="Название сегмента (напр. Я.Директ)"/>
            )}
            <button className="btn btn-sm" style={{ flex: "0 0 auto" }} onClick={() => removeSeg(si)}>Удалить</button>
          </div>

          {!isCollapsed && (<>
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 10, flexWrap: "wrap" }}>
            <span style={{ ...sectionTitle, margin: 0 }}>UTM-поля</span>
            <div style={{ display: "inline-flex", gap: 0, border: "1px solid var(--line)", borderRadius: 8, overflow: "hidden" }}>
              {([["any", "ИЛИ (любое)"], ["all", "И (все)"]] as ["any" | "all", string][]).map(([m, label]) => (
                <button key={m} onClick={() => patchSeg(si, { match: m })}
                        style={{
                          padding: "5px 12px", fontSize: 12, cursor: "pointer", border: "none",
                          background: seg.match === m ? GREEN : "var(--card)",
                          color: seg.match === m ? "#fff" : "var(--ink)",
                        }}>
                  {label}
                </button>
              ))}
            </div>
            <span style={{ fontSize: 11.5, color: "var(--mute)" }}>
              {seg.match === "all" ? "сделка должна подходить под ВСЕ правила" : "сделка подходит под ЛЮБОЕ правило"}
            </span>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 10 }}>
            {seg.rules.map((r, ri) => (
              <RuleRow key={ri} rule={r} fields={fields}
                       onChange={patch => patchSeg(si, { rules: seg.rules.map((x, j) => j === ri ? { ...x, ...patch } : x) })}
                       onRemove={() => patchSeg(si, { rules: seg.rules.filter((_, j) => j !== ri) })}/>
            ))}
          </div>
          <button className="btn btn-sm" onClick={() => patchSeg(si, { rules: [...seg.rules, { field_id: null, values: "" }] })}>+ поле</button>

          <div style={{ ...sectionTitle, marginTop: 18 }}>Этапы воронки этого сегмента</div>
          {stagesQ.isLoading ? <div style={{ display: "flex", justifyContent: "center", padding: "28px 12px" }}><Spinner label="Загрузка…"/></div> : (
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {[...byPipeline.entries()].map(([pname, arr]) => (
                <div key={pname}>
                  <div style={{ fontSize: 11.5, color: "var(--mute)", marginBottom: 6 }}>{pname}</div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                    {arr.map(st => {
                      const on = seg.stageIds.has(st.stage_id);
                      return (
                        <button key={st.stage_id} style={chipStyle(on, st.color)}
                                onClick={() => { const n = new Set(seg.stageIds); n.has(st.stage_id) ? n.delete(st.stage_id) : n.add(st.stage_id); patchSeg(si, { stageIds: n }); }}>
                          {on ? "✓ " : ""}{st.name}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
          </>)}
        </div>
        );
      })}

      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
        <button className="btn btn-sm" onClick={addSeg}>+ Добавить сегмент</button>
        <button className="btn btn-sm btn-primary" disabled={saveMut.isPending} onClick={() => saveMut.mutate()}>
          {saveMut.isPending ? "Сохраняю…" : "Сохранить"}
        </button>
        {saveMut.isSuccess && <span style={{ fontSize: 12, color: GREEN }}>Сохранено</span>}
        {saveMut.isError && <span style={{ fontSize: 12, color: "var(--brick)" }}>{(saveMut.error as Error).message}</span>}
      </div>
    </div>
  );
}

// --- Целевые показатели (таргеты) по источнику + кабинету ---
function TargetsTab() {
  const qc = useQueryClient();
  const accountsQ = useQuery({ queryKey: ["marketing/accounts"], queryFn: fetchMarketingAccounts });
  const accounts = accountsQ.data ?? [];
  const source = "yandex_direct";
  const [cabinet, setCabinet] = useState<number | null>(null);
  const overviewQ = useQuery({ queryKey: ["marketing/overview-targets"], queryFn: () => fetchMarketingOverview({}) });
  const goals = overviewQ.data?.goals ?? [];
  const customMetrics = useMemo(() => {
    const load = (k: string): any[] => { try { return JSON.parse(localStorage.getItem(k) || "[]"); } catch { return []; } };
    const seen = new Set<string>(); const out: { id: string; label: string; fmt: string }[] = [];
    for (const m of [...load("mkt_cols_analytics_customcols"), ...load("mkt_cols_dash_customcols")]) {
      if (m?.id && !seen.has(m.id)) { seen.add(m.id); out.push({ id: m.id, label: m.label, fmt: m.fmt }); }
    }
    return out;
  }, []);
  const targetsQ = useQuery({ queryKey: ["marketing/targets", cabinet], queryFn: () => fetchTargets(source, cabinet) });
  const [goalT, setGoalT] = useState<Record<string, string>>({});
  const [metricT, setMetricT] = useState<Record<string, string>>({});
  useEffect(() => {
    if (!targetsQ.data) return;
    setGoalT(Object.fromEntries(Object.entries(targetsQ.data.goals).map(([k, v]) => [k, String(v)])));
    setMetricT(Object.fromEntries(Object.entries(targetsQ.data.metrics).map(([k, v]) => [k, String(v)])));
  }, [targetsQ.data]);
  const toNum = (o: Record<string, string>): Record<string, number> => {
    const r: Record<string, number> = {};
    for (const [k, v] of Object.entries(o)) { const n = parseFloat(String(v).replace(",", ".")); if (v.trim() && !isNaN(n)) r[k] = n; }
    return r;
  };
  const saveMut = useMutation({
    mutationFn: () => saveTargets(source, cabinet, toNum(goalT), toNum(metricT)),
    onSuccess: d => qc.setQueryData(["marketing/targets", cabinet], d),
  });
  const fmtHint = (f: string) => f === "rub" ? "₽" : f === "pct" ? "%" : "число";

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <p style={{ ...muted, margin: 0 }}>
        Задай целевую стоимость по каждой цели и по кастомным метрикам — для источника и кабинета.
        ИИ-анализ будет подсвечивать показатели <strong>относительно этих таргетов</strong>, а не по общим нормативам.
      </p>
      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
        <span style={{ fontSize: 12, color: "var(--mute)" }}>Источник: <strong>Яндекс.Директ</strong> · Кабинет:</span>
        <button className={`btn btn-sm ${cabinet === null ? "btn-primary" : ""}`} onClick={() => setCabinet(null)}>Общий (все)</button>
        {accounts.map(a => (
          <button key={a.id} className={`btn btn-sm ${cabinet === a.id ? "btn-primary" : ""}`} onClick={() => setCabinet(a.id)}>{a.title ? `${a.title} · ${a.login}` : a.login}</button>
        ))}
      </div>

      <div className="card" style={cardStyle}>
        <div style={{ ...sectionTitle, marginBottom: 10 }}>Цели Я.Метрики — целевой CPA (₽)</div>
        {goals.length === 0 ? <div style={muted}>Целей нет (синхронизируй статистику с целями).</div> : (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {goals.map(g => (
              <div key={g.goal_id} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <span style={{ flex: 1, minWidth: 0, fontSize: 13, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{g.name}</span>
                <input className="input-sm" type="text" placeholder="напр. 1000" value={goalT[g.goal_id] ?? ""}
                       onChange={e => setGoalT(p => ({ ...p, [g.goal_id]: e.target.value }))} style={{ width: 120 }}/>
                <span style={{ fontSize: 12, color: "var(--mute)", width: 14 }}>₽</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="card" style={cardStyle}>
        <div style={{ ...sectionTitle, marginBottom: 10 }}>Кастомные метрики — целевое значение</div>
        {customMetrics.length === 0 ? <div style={muted}>Кастомных метрик пока нет (создай в таблице кампаний).</div> : (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {customMetrics.map(m => (
              <div key={m.id} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <span style={{ flex: 1, minWidth: 0, fontSize: 13, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{m.label}</span>
                <input className="input-sm" type="text" placeholder="цель" value={metricT[m.id] ?? ""}
                       onChange={e => setMetricT(p => ({ ...p, [m.id]: e.target.value }))} style={{ width: 120 }}/>
                <span style={{ fontSize: 12, color: "var(--mute)", width: 36 }}>{fmtHint(m.fmt)}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        <button className="btn btn-sm btn-primary" disabled={saveMut.isPending} onClick={() => saveMut.mutate()}>
          {saveMut.isPending ? "Сохраняю…" : "Сохранить таргеты"}
        </button>
        {saveMut.isSuccess && <span style={{ fontSize: 12, color: GREEN }}>Сохранено · ИИ учтёт при следующем анализе</span>}
      </div>
    </div>
  );
}

export default MarketingSettingsPage;
